import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './VerifyEmail.css';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error, expired
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setMessage('No verification token provided. Please check your email and try again.');
        setIsLoading(false);
        return;
      }

      try {
        // Get stored user data (check both 'user' and 'pendingVerification')
        let storedUser = localStorage.getItem('user');
        let userData = null;
        let isPendingVerification = false;
        
        // First try to get from 'user' (existing users)
        if (storedUser) {
          userData = JSON.parse(storedUser);
        } else {
          // Then try to get from 'pendingVerification' (new registrations)
          const pendingUser = localStorage.getItem('pendingVerification');
          if (pendingUser) {
            userData = JSON.parse(pendingUser);
            isPendingVerification = true;
          }
        }
        
        if (!userData || !userData.email) {
          setStatus('error');
          setMessage('No user account found. Please register first.');
          setIsLoading(false);
          return;
        }
        
        // Simple token validation - check format
        if (!token.startsWith('REG-') || token.length < 10) {
          setStatus('error');
          setMessage('Invalid verification token format.');
          setIsLoading(false);
          return;
        }

        // Check if user is already verified
        if (userData.verified) {
          setStatus('error');
          setMessage('Your email is already verified. You can proceed to login.');
          setIsLoading(false);
          return;
        }

        // Check if token matches OR if the token in URL is newer than stored token
        // This handles the case when user requested a new verification email
        const storedTokenTimestamp = userData.verificationToken 
          ? parseInt(userData.verificationToken.split('-')[1] || '0') 
          : 0;
        const urlTokenTimestamp = parseInt(token.split('-')[1] || '0');
        
        const isTokenValid = userData.verificationToken === token;
        const isNewerToken = urlTokenTimestamp > storedTokenTimestamp;
        
        if (isTokenValid || isNewerToken) {
          // Update user verification status
          const updatedUser = {
            ...userData,
            verified: true,
            verificationToken: null, // Clear the token after successful verification
            verifiedAt: new Date().toISOString()
          };
          
          // Move from pendingVerification to user (if coming from pendingVerification)
          if (isPendingVerification) {
            localStorage.removeItem('pendingVerification');
          }
          localStorage.setItem('user', JSON.stringify(updatedUser));
          
          setStatus('success');
          setMessage('Your email has been successfully verified! Welcome to KingSolarman.');
          setIsLoading(false);

          // Redirect to account page after 3 seconds
          setTimeout(() => {
            navigate('/account');
          }, 3000);
          
        } else {
          // Token is expired - show option to request new one
          setStatus('expired');
          setMessage('This verification link has expired. Please request a new verification email.');
          setIsLoading(false);
        }

      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage('An error occurred during verification. Please try again later.');
        setIsLoading(false);
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  const handleResendEmail = async () => {
    setIsLoading(true);
    
    // Try to get user data from either location
    let userData = null;
    
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      userData = JSON.parse(storedUser);
    } else {
      const pendingUser = localStorage.getItem('pendingVerification');
      if (pendingUser) {
        userData = JSON.parse(pendingUser);
      }
    }
    
    if (!userData || !userData.email) {
      alert('No user found. Please register first.');
      setIsLoading(false);
      return;
    }
    
    try {
      // Call the backend API to resend verification email
      const API_BASE_URL = process.env.REACT_APP_EMAIL_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE_URL}/api/email/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: userData.email,
          firstName: userData.firstName || userData.name?.split(' ')[0] || '',
          lastName: userData.lastName || userData.name?.split(' ').slice(1).join(' ') || ''
        })
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        // Update local storage with new verification token
        const updatedUser = {
          ...userData,
          verificationToken: result.verificationToken,
          registrationTime: new Date().toISOString()
        };
        
        // Update storage
        const pendingUser = localStorage.getItem('pendingVerification');
        if (pendingUser) {
          localStorage.setItem('pendingVerification', JSON.stringify(updatedUser));
        } else {
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
        
        alert(`✅ Verification email sent to ${userData.email}! Please check your inbox and click the verification link.`);
      } else {
        alert(`❌ Failed to send email: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Resend email error:', error);
      alert('❌ Failed to send verification email. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="verify-email-page-container">
          <div className="verify-email-page-content">
            <div className="verify-email-loading-spinner"></div>
            <h2>Verifying your email...</h2>
            <p>Please wait while we verify your email address.</p>
          </div>
        </div>
      );
    }

    switch (status) {
      case 'success':
        return (
          <div className="verify-email-page-container">
            <div className="verify-email-page-content verify-email-success">
              <div className="verify-email-success-icon">✅</div>
              <h2>Email Verified Successfully!</h2>
              <p>{message}</p>
              <div className="verify-email-redirect-info">
                <p>Redirecting you to your account page...</p>
                <div className="verify-email-progress-bar">
                  <div className="verify-email-progress-fill"></div>
                </div>
              </div>
              <button 
                className="verify-email-btn verify-email-btn-primary"
                onClick={() => navigate('/account')}
              >
                Go to Account Now
              </button>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="verify-email-page-container">
            <div className="verify-email-page-content verify-email-error">
              <div className="verify-email-error-icon">❌</div>
              <h2>Verification Failed</h2>
              <p>{message}</p>
              <div className="verify-email-action-buttons">
                <button 
                  className="verify-email-btn verify-email-btn-secondary"
                  onClick={() => navigate('/account')}
                >
                  Go to Account
                </button>
                <button 
                  className="verify-email-btn verify-email-btn-outline"
                  onClick={handleResendEmail}
                >
                  Resend Verification Email
                </button>
              </div>
            </div>
          </div>
        );

      case 'expired':
        return (
          <div className="verify-email-page-container">
            <div className="verify-email-page-content verify-email-expired">
              <div className="verify-email-expired-icon">⏰</div>
              <h2>Verification Link Expired</h2>
              <p>{message}</p>
              <div className="verify-email-action-buttons">
                <button 
                  className="verify-email-btn verify-email-btn-primary"
                  onClick={handleResendEmail}
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : '📧 Request New Verification Email'}
                </button>
                <button 
                  className="verify-email-btn verify-email-btn-secondary"
                  onClick={() => navigate('/')}
                >
                  Back to Home
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="verify-email-page-container">
            <div className="verify-email-page-content">
              <h2>Email Verification</h2>
              <p>Processing your email verification...</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="verify-email-page-wrapper">
      {renderContent()}
    </div>
  );
};

export default VerifyEmail;

